<template>
  <header class="header">
    <div class="container">
      <div class="header-content">
        <router-link to="/">
          <img alt="Winn Digital logo" src="./assets/logo.png" class="logo" />
        </router-link>
        <div class="nav-links">
          <router-link to="/" :class="['nav-link', { active: isActive('/') }]"
            >Home</router-link
          >
          <router-link
            to="/services"
            :class="['nav-link', { active: isActive('/services') }]"
          >
            Services</router-link
          >
          <router-link
            to="/about"
            :class="['nav-link', { active: isActive('/about') }]"
          >
            About</router-link
          >
          <router-link
            to="/contact"
            :class="['nav-link', { active: isActive('/contact') }]"
          >
            Contact</router-link
          >
        </div>

        <router-link to="/contact" class="nav-cta"
          >Free Strategy Call</router-link
        >

        <!-- Hamburger -->
        <button class="hamburger" @click="toggleMenu" aria-label="Menu">
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
          <span :class="{ open: menuOpen }"></span>
        </button>

        <!-- Mobile Menu -->
        <transition name="fade-slide">
          <div v-if="menuOpen" class="mobile-menu">
            <router-link
              @click="closeMenu"
              to="/"
              :class="['nav-link', { active: isActive('/') }]"
            >
              Home
            </router-link>
            <router-link
              @click="closeMenu"
              to="/services"
              :class="['nav-link', { active: isActive('/services') }]"
            >
              Services
            </router-link>
            <router-link
              @click="closeMenu"
              to="/about"
              :class="['nav-link', { active: isActive('/about') }]"
            >
              About
            </router-link>
            <router-link
              @click="closeMenu"
              to="/contact"
              :class="['nav-link', { active: isActive('/contact') }]"
            >
              Contact
            </router-link>
            <router-link to="/contact" class="btn-primary mobile-cta">
              Free Strategy Call
            </router-link>
          </div>
        </transition>
      </div>
    </div>
  </header>
  <router-view />

  <!-- Footer -->
  <footer class="footer">
    <div class="container">
      <div class="footer-content">
        <div class="footer-logo">
          <img alt="Winn Digital logo" src="./assets/logo.png" class="logo" />
        </div>

        <a :href="`mailto:info@winndigital.com`" class="link"
          >info@winndigital.com</a
        >
        <br />
        <a :href="`tel:435-429-1141`" class="link">(435) 429-1141</a>
        <p class="footer-tagline">
          Custom Web Development, Integrations/ Automations, and Digital
          Strategy for Businesses.
        </p>
        <p>
          Based in Southern Utah - Proudly partnering with businesses in - St.
          George, Washington, Hurricane, Cedar City, and across the United
          States.
        </p>
        <router-link to="/privacy">Privacy Policy</router-link>
        <br />
        <router-link to="/terms">Terms & Conditions</router-link>
        <br />
        <p class="footer-copyright">© 2026 Winn Digital. <br /></p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";

const menuOpen = ref(false);
import { useRoute } from "vue-router";

const route = useRoute();

const isActive = (path: string) => route.path === path;

const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const handleResize = () => {
  if (window.innerWidth > 900) {
    menuOpen.value = false;
  }
};

// Header scroll effect

onMounted(() => {
  const header = document.querySelector("header");
  window.addEventListener("resize", handleResize);
  window.addEventListener("scroll", () => {
    if (window.scrollY > 50) {
      header.classList.add("scrolled");
    } else {
      header.classList.remove("scrolled");
    }
  });
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>

<style type="css">
@import url("https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
html {
  scroll-behavior: smooth;
}
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: "Lato", sans-serif;
  color: #ffffff;
  background: #000000;
  line-height: 1.6;
}

.container {
  max-width: 1300px;
  margin: 0 auto;
  padding: 25px 20px;
}

/* Header */
header {
  background: transparent;
  padding: 5px 0;
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid transparent;
  transition: all 0.3s ease;
}
@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-up {
  opacity: 0;
  animation: fadeUp 0.7s ease forwards;
}

.delay-1 {
  animation-delay: 0.1s;
}
.delay-2 {
  animation-delay: 0.2s;
}
.delay-3 {
  animation-delay: 0.3s;
}
.delay-4 {
  animation-delay: 0.4s;
}
.delay-5 {
  animation-delay: 0.5s;
}
.hamburger {
  display: none;
  margin-right: 40px;
  background: none;
  border: none;
  cursor: pointer;

  flex-direction: column;
  gap: 6px;
}

.hamburger span {
  width: 26px;
  height: 2px;
  background: white;
  transition: all 0.3s ease;
}

.hamburger span.open:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}

.hamburger span.open:nth-child(2) {
  opacity: 0;
}

.hamburger span.open:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}
.mobile-menu {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;

  background: rgba(10, 6, 32, 0.95);
  backdrop-filter: blur(16px);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 40px 0;
}
.link {
  color: #8a2be2;
  text-decoration: none;
  font-weight: 700;
}
.mobile-menu a {
  font-size: 20px;
  color: white;
  text-decoration: none;
}

.mobile-cta {
  margin-top: 10px;
}
header.scrolled {
  background: rgba(0, 0, 0, 0.92);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(108, 59, 255, 0.22);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.nav-links {
  display: flex;
  gap: 52px;
  align-items: center;
}

.nav-link {
  color: #b0b0b0;
  text-decoration: none;
  font-weight: 400;
  transition: color 0.3s ease;
  position: relative;
}
.nav-link::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #6c3bff, #8b5cf6);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.nav-link:hover,
.nav-link.active {
  color: #ffffff;
}

.nav-link:hover::after,
.nav-link.active::after {
  width: 100%;
}

.nav-cta {
  background: linear-gradient(135deg, #6c3bff 0%, #8b5cf6 100%);
  color: #ffffff;
  padding: 12px 28px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 700;
  transition: all 0.3s ease;
  box-shadow: 0 0 22px rgba(108, 59, 255, 0.35);
}

.nav-cta:hover {
  box-shadow: 0 0 38px rgba(108, 59, 255, 0.6);
  transform: translateY(-2px);
}

.logo {
  height: 40px;
}

/* Hero Section */
.hero {
  background: url("./assets/hero.png");
  background-size: cover;
  background-position: center;
  padding: 180px 0 190px;
  text-align: left;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background: radial-gradient(
      circle at 20% 50%,
      rgba(138, 43, 226, 0.2) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 50%,
      rgba(138, 43, 226, 0.2) 0%,
      transparent 50%
    ); */
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
  max-width: 800px;
}

h1 {
  font-size: 60px;
  font-weight: 900;
  line-height: 1.2;
  margin-bottom: 24px;
  color: #ffffff;
}

.hero-subheadline {
  font-size: 22px;
  color: #cccccc;
  max-width: 750px;
  margin: 0 0 40px 0;
  font-weight: 300;
}

.cta-group {
  display: flex;
  gap: 20px;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.btn-primary {
  background: linear-gradient(135deg, #8a2be2 0%, #8b5cf6 100%);
  color: #ffffff;
  padding: 18px 40px;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.3s ease;
  display: inline-block;
  box-shadow: 0 0 26px rgba(108, 59, 255, 0.42);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 0 48px rgba(108, 59, 255, 0.68);
}

.btn-secondary {
  background: transparent;
  color: #ffffff;
  padding: 18px 40px;
  border: 2px solid #8b5cf6;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 700;
  font-size: 18px;
  transition: all 0.3s ease;
  display: inline-block;
}

.btn-secondary:hover {
  background: rgba(108, 59, 255, 0.1);
  border-color: #6c3bff;
  box-shadow: 0 0 22px rgba(108, 59, 255, 0.25);
  transform: translateY(-2px);
}

/* Section Styles */
section {
  padding: 50px 0;
}

.section-dark {
  background: linear-gradient(160deg, #0d0420 0%, #060606 65%);
}

.section-light {
  background: #ffffff;
  color: #0a0a0a;
}

.section-accent {
  background: linear-gradient(135deg, #1a0033 0%, #0a0a0a 100%);
}

.section-title {
  font-size: 42px;
  font-weight: 900;
  text-align: center;
  margin-bottom: 20px;
  color: #ffffff;
  letter-spacing: -0.02em;
}

.section-title.dark {
  color: #0a0a0a;
}

.section-subtitle {
  font-size: 20px;
  text-align: center;
  color: #999999;
  max-width: 700px;
  margin: 0 auto 60px;
  font-weight: 300;
  line-height: 1.75;
}

.section-subtitle.dark {
  color: #555555;
}

.section-subtitle-2 {
  font-size: 22px;
  text-align: center;
  color: white;
  max-width: 900px;
  margin: 0 auto 60px;
  font-weight: 300;
}
/* Problem Section */
.problem-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
  margin-top: 60px;
}

.problem-card {
  background: rgba(108, 59, 255, 0.04);
  border: 1px solid rgba(108, 59, 255, 0.18);
  padding: 40px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.problem-card:hover {
  border-color: rgba(108, 59, 255, 0.5);
  background: rgba(108, 59, 255, 0.08);
  transform: translateY(-4px);
}

.problem-card h3 {
  font-size: 24px;
  margin-bottom: 16px;
  color: #8a2be2;
}

.problem-card p {
  color: #cccccc;
  line-height: 1.8;
}

/* Guide Section */
.guide-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  margin-top: 60px;
}

.guide-text h3 {
  font-size: 32px;
  margin-bottom: 24px;
  color: #ffffff;
}

.guide-features {
  list-style: none;
  padding: 0px;
}

.guide-features li {
  padding: 20px 0;
  border-bottom: 1px solid rgba(138, 43, 226, 0.2);
  color: #cccccc;
  font-size: 18px;
  display: flex;
  gap: 5px;
}

.guide-features li:last-child {
  border-bottom: none;
}

.guide-features li::before {
  content: "✓";
  color: #8a2be2;
  font-weight: 900;
  margin-right: 12px;
  font-size: 20px;
}

.guide-visual {
  border-radius: 8px;
  padding: 20px 40px;
  text-align: center;
  font-size: 18px;
  color: #aaaaaa;
}
.guide-visual img {
  max-width: 530px;
  width: 100%;
  border-radius: 4px;
}

/* Plan Section */
.plan-steps {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 60px;
}

.step {
  text-align: center;
  position: relative;
}

.step-number {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6c3bff 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  font-weight: 900;
  margin: 0 auto 24px;
  box-shadow: 0 0 30px rgba(108, 59, 255, 0.45);
}

.step h3 {
  font-size: 22px;
  margin-bottom: 16px;
  color: #ffffff;
}

.step p {
  color: #cccccc;
  line-height: 1.8;
}

.plan-cta {
  text-align: center;
  margin-top: 60px;
}

/* Services Section */

.services-grid-3 {
  grid-template-columns: repeat(3, 1fr);
}

.service-card-v2 {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.service-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.3rem;
  padding-top: 0.9rem;
  width: 100%;
}

.service-pill {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.22rem 0.7rem;
  background: rgba(138, 43, 226, 0.12);
  color: rgba(255, 255, 255, 0.6);
  border-radius: 4px;
  border: 1px solid rgba(138, 43, 226, 0.22);
}

@media (max-width: 768px) {
  .services-grid-3 {
    grid-template-columns: 1fr;
  }
}
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 50px;
  margin-top: 20px;
}

.service-card {
  background: rgba(0, 0, 0, 0.5);
  border: 2px solid rgba(138, 43, 226, 0.3);
  border-radius: 8px;
  padding: 25px 35px;
  transition: all 0.3s ease;
  text-decoration: none;
}
.highlight {
  font-style: italic;
  background: linear-gradient(135deg, #8a2be2, #8b5cf6, #c084fc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.service-card:hover {
  border-color: #8a2be2;
  transform: translateY(-8px);
  background: rgba(138, 43, 226, 0.05);
}

.service-icon {
  font-size: 48px;
  margin-bottom: 24px;
}

.service-card h3 {
  font-size: 24px;
  margin-bottom: 20px;
  color: #ffffff;
}

.service-card p {
  color: #cccccc;
  line-height: 1.8;
  margin-bottom: 20px;
}

.service-card ul {
  list-style: none;
  color: #aaaaaa;
}

.service-card ul li {
  padding: 8px 0;
  padding-left: 24px;
  position: relative;
}

.service-card ul li::before {
  content: "→";
  position: absolute;
  left: 0;
  color: #8a2be2;
  font-weight: 700;
}

/* Stakes Section */
.stakes-content {
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}

.stakes-list {
  text-align: left;
  margin: 40px 0;
}

.stakes-item {
  background: rgba(220, 50, 50, 0.04);
  border-left: 4px solid #dc3232;
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 0 8px 8px 0;
}

.stakes-item h4 {
  color: #ff4d4d;
  font-size: 20px;
  margin-bottom: 12px;
}

.stakes-item p {
  color: #cccccc;
  line-height: 1.8;
}

/* Success Section */
.success-content {
  max-width: 820px;
  margin: 0 auto;
  text-align: center;
}

.success-list {
  text-align: left;
  margin: 40px 0;
}

.success-item {
  background: rgba(138, 43, 226, 0.05);
  border-left: 4px solid #8a2be2;
  padding: 24px;
  margin-bottom: 20px;
  border-radius: 4px;
}

.success-item h4 {
  color: #8a2be2;
  font-size: 20px;
  margin-bottom: 12px;
}

.success-item p {
  color: #cccccc;
  line-height: 1.8;
}

/* Final CTA Section */
.final-cta {
  background: linear-gradient(135deg, #8a2be2 20%, #6c3bff 50%, #8b5cf6 100%);
  padding: 120px 0;
  padding: 100px 0;
  text-align: center;
}
.final-cta::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at 50% 50%,
    rgba(255, 255, 255, 0.07) 0%,
    transparent 65%
  );
  pointer-events: none;
}
.final-cta h2 {
  font-size: 48px;
  margin-bottom: 24px;
}

.final-cta p {
  font-size: 22px;
  margin-bottom: 40px;
  opacity: 0.95;
}

/* Footer */
footer {
  background: #0a0a0a;
  padding: 60px 0 30px;
  border-top: 1px solid rgba(138, 43, 226, 0.2);
}

.footer-content {
  text-align: center;
  color: #666666;
}

.footer-logo {
  font-size: 28px;
  font-weight: 900;
  color: #8a2be2;
  margin-bottom: 20px;
}

.footer-content p {
  margin-bottom: 10px;
}

/* Responsive */
@media (max-width: 968px) {
  .hamburger {
    display: flex;
  }
  h1 {
    font-size: 42px;
  }
  .hero-subheadline {
    font-size: 18px;
  }

  .guide-content,
  .plan-steps {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 36px;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }
  .contact-form {
    padding: 40px;
  }

  .nav-links,
  .desktop-cta,
  .nav-cta {
    display: none;
  }
  .logo {
    height: 40px;
  }
}

@media (max-width: 640px) {
  h1 {
    font-size: 32px;
  }

  .btn-primary,
  .btn-secondary {
    padding: 14px 28px;
    font-size: 16px;
  }

  .cta-group {
    flex-direction: column;
    align-items: stretch;
  }
  .contact-form {
    padding: 30px 20px;
  }
}
</style>
